import { throttle, debounce } from 'lodash';

enum ScrollDirection {
  UP = 'up',
  DOWN = 'down',
}

const SCROLL_OFFSET = 10;

class TheViewInnerHero {
  element: HTMLElement;
  lastScrollTop: number;
  scrollOffset: number;
  direction: ScrollDirection;
  beginScrollDownValue: number;
  resizeHandler: () => void;
  alignmentHandler: () => void;
  linksContainer: HTMLElement | null;
  logoContainer: HTMLElement | null;
  lastMarginEnd: string = '';
  isResizing: boolean = false;
  isResized: boolean = false;
  resizeTimeout: number | null = null;
  lastKnownScrollY: number;
  isResetToTop: boolean = false;
  lastDirectionChangeTime: number = 0;

  constructor(element: HTMLElement) {
    this.element = element;
    this.lastScrollTop = 0;
    this.scrollOffset = 0;
    this.beginScrollDownValue = 0;
    this.direction = ScrollDirection.DOWN;
    this.resizeHandler = this.handleResize.bind(this);
    this.alignmentHandler = debounce(this.adjustLinksAlignment.bind(this), 0);
    this.linksContainer = element.querySelector('.kf-o-the-view-inner-hero__links');
    this.logoContainer = element.querySelector('.kf-o-the-view-inner-hero__logo');
    this.lastMarginEnd = '';
    this.isResizing = false;
    this.isResized = false;
    this.resizeTimeout = null;
    this.lastKnownScrollY = window.scrollY;
    this.isResetToTop = false;
  }

  init() {
    this.handleInitScrollDirection();
    this.adjustLinksAlignment();
    this.setVariableHeight();
    this.attachEvents();
  }

  attachEvents() {
    window.addEventListener('scroll', throttle(this.handleScroll.bind(this), 10));
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('resize', this.alignmentHandler);
  }

  handleInitScrollDirection() {
    if (window.scrollY > 0) {
      this.element.dataset.scrollDirection = ScrollDirection.DOWN;
      this.element.dataset.isSticky = 'true';
    }
  }

  handleScroll() {
    const currentScrollY = window.scrollY;

    if (this.isResizing) {
      return;
    }

    const scrollDifference = Math.abs(currentScrollY - this.lastKnownScrollY);

    if (scrollDifference < SCROLL_OFFSET) {
      return;
    }

    const rect = this.element.getBoundingClientRect();

    this.lastKnownScrollY = currentScrollY;

    if (rect.top > 0 || currentScrollY === 0) {
      if (!this.isResetToTop) {
        this.instantResetToTop();
      }

      return;
    }

    if (this.isResetToTop && rect.top <= 0 && currentScrollY > 0) {
      this.isResetToTop = false;
    }

    const { direction, isScrollOffsetUpdated } = this.getScrollDirection();

    if (isScrollOffsetUpdated && (this.direction !== direction || !this.element.dataset.scrollDirection)) {
      // Add a small debounce to prevent rapid direction changes
      const now = Date.now();
      const timeSinceLastChange = now - this.lastDirectionChangeTime;

      if (timeSinceLastChange > 100) {
        // 100ms debounce
        this.element.dataset.scrollDirection = direction;
        this.direction = direction;
        this.beginScrollDownValue = this.lastScrollTop;
        this.lastDirectionChangeTime = now;
      }
    }

    this.element.dataset.isSticky = 'true';
  }

  getScrollDirection() {
    const scrollTop = window.scrollY;
    let direction = ScrollDirection.UP;

    if (scrollTop > this.lastScrollTop) {
      direction = ScrollDirection.DOWN;
    }

    this.scrollOffset = Math.abs(this.beginScrollDownValue - scrollTop);
    const isScrollOffsetUpdated = this.scrollOffset >= SCROLL_OFFSET;

    this.lastScrollTop = scrollTop;

    return {
      direction,
      isScrollOffsetUpdated,
    };
  }

  setVariableHeight() {
    const logoWrapper = this.element.querySelector<HTMLElement>('.kf-o-the-view-inner-hero__logo-wrapper');
    const linksWrapper = this.element.querySelector<HTMLElement>('.kf-o-the-view-inner-hero__links-wrapper');

    if (logoWrapper && linksWrapper) {
      const logoRect = logoWrapper.getBoundingClientRect();
      const linksRect = linksWrapper.getBoundingClientRect();
      const totalHeight = logoRect.height + linksRect.height;

      this.element.style.setProperty('--height', `${totalHeight}px`);
    }
  }

  handleResize() {
    const rect = this.element.getBoundingClientRect();

    if (rect.top > 0) {
      this.setVariableHeight();
    }

    this.isResizing = true;
    this.isResized = true;
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = window.setTimeout(() => {
      this.isResizing = false;
      this.resizeTimeout = null;
    }, 200);
  }

  detachEvents() {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('resize', this.alignmentHandler);
  }

  instantResetToTop() {
    this.isResetToTop = true;

    this.element.classList.add('no-transition');

    this.element.removeAttribute('data-scroll-direction');
    this.element.removeAttribute('data-is-sticky');

    this.beginScrollDownValue = 0;
    this.lastScrollTop = 0;
    this.direction = ScrollDirection.DOWN;
    this.lastDirectionChangeTime = 0; // Reset debounce timer
    this.setVariableHeight();

    setTimeout(() => {
      this.element.classList.remove('no-transition');
    }, 150);
  }

  adjustLinksAlignment() {
    if (!this.linksContainer || !this.logoContainer) {
      return;
    }

    // Tablet and Desktop always center and last link margin end is 0
    if (window.innerWidth >= 768) {
      if (this.linksContainer.style.getPropertyValue('--links-justify-content') !== 'center') {
        this.linksContainer.style.setProperty('--links-justify-content', 'center');
      }
      if (this.linksContainer.style.getPropertyValue('--links-margin-end') !== '0') {
        this.linksContainer.style.setProperty('--links-margin-end', '0');
      }
      if (this.lastMarginEnd !== '0') {
        this.linksContainer.style.setProperty('--links-last-margin', '0');
        this.lastMarginEnd = '0';
      }

      return;
    }

    // Mobile - adjust based on logo and links width
    const logoWidth = this.logoContainer.offsetWidth;
    const links = this.linksContainer.querySelectorAll('.kf-a-link');
    let totalLinksWidth = 0;

    links.forEach((link) => {
      totalLinksWidth += (link as HTMLElement).offsetWidth;
    });

    const shouldCenter = totalLinksWidth <= logoWidth;

    this.linksContainer.style.setProperty('--links-justify-content', shouldCenter ? 'center' : 'flex-start');
    this.linksContainer.style.setProperty('--links-margin-end', shouldCenter ? '32px' : '0');
    this.linksContainer.style.setProperty('--links-last-margin', shouldCenter ? '0' : '32px');
    this.lastMarginEnd = shouldCenter ? '0' : '32px';
  }
}

export default TheViewInnerHero;
